import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { mediaUrl } from "../../../utils/clientHeroTheme";
import { personDisplayName } from "../../../utils/clientAppUtils";
import { useIsMobile } from "../../../hooks/useMediaQuery";
import { Avatar } from "../../ui";
import s from "../clinic.module.css";

function previewText(text, max = 180) {
  const t = String(text || "").trim();
  if (!t) return "";
  return t.length > max ? `${t.slice(0, max).trim()}…` : t;
}

function BackIcon() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" aria-hidden>
      <path
        d="M14.5 6.5 9 12l5.5 5.5"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function RepostPreview({ post }) {
  const authorName = personDisplayName(post.author?.full_name) || "Автор";
  const previewBody = previewText(post.body || post.title);
  const previewTitle = post.title?.trim() || null;
  const previewPhoto = (post.media || []).find((m) => m.type !== "video");

  return (
    <div className={s.repostSheetPreviewCard}>
      <div className={s.repostSheetPreviewHead}>
        <Avatar
          name={authorName}
          src={mediaUrl(post.author?.photo_url)}
          size="sm"
        />
        <strong>{authorName}</strong>
      </div>
      {previewTitle ? (
        <p className={s.repostSheetPreviewTitle}>{previewTitle}</p>
      ) : null}
      {!previewTitle && previewBody ? (
        <p className={s.repostSheetPreviewBody}>{previewBody}</p>
      ) : previewBody && previewTitle ? (
        <p className={s.repostSheetPreviewBody}>{previewBody}</p>
      ) : null}
      {previewPhoto?.url ? (
        <img
          src={mediaUrl(previewPhoto.url)}
          alt=""
          className={s.repostSheetPreviewImg}
        />
      ) : null}
    </div>
  );
}

export default function ClubLifeRepostModal({
  post,
  open,
  busy = false,
  onClose,
  onConfirm,
}) {
  const isMobile = useIsMobile(768);
  const [comment, setComment] = useState("");
  const inputRef = useRef(null);

  useEffect(() => {
    if (open) {
      setComment("");
      const t = window.setTimeout(() => inputRef.current?.focus(), 120);
      return () => window.clearTimeout(t);
    }
    return undefined;
  }, [open, post?.id]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape" && !busy) onClose?.();
    };
    document.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
  }, [open, busy, onClose]);

  if (!open || !post) return null;

  const submit = () => {
    if (busy) return;
    onConfirm(comment.trim());
  };

  if (!isMobile) {
    return createPortal(
      <div
        className={s.repostDialogOverlay}
        role="presentation"
        onClick={() => !busy && onClose?.()}
      >
        <div
          className={s.repostDialog}
          role="dialog"
          aria-modal="true"
          aria-labelledby="repost-dialog-title"
          onClick={(e) => e.stopPropagation()}
        >
          <header className={s.repostDialogHead}>
            <h2 id="repost-dialog-title" className={s.repostDialogTitle}>
              Репост
            </h2>
            <button
              type="button"
              className={s.repostDialogClose}
              onClick={onClose}
              disabled={busy}
              aria-label="Закрыть"
            >
              ×
            </button>
          </header>

          <div className={s.repostDialogBody}>
            <textarea
              ref={inputRef}
              id="repost-comment"
              className={s.repostDialogInput}
              rows={4}
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              placeholder="Поделитесь своими мыслями…"
              maxLength={2000}
              disabled={busy}
            />
            <RepostPreview post={post} />
          </div>

          <footer className={s.repostDialogFoot}>
            <button
              type="button"
              className={s.repostDialogCancel}
              onClick={onClose}
              disabled={busy}
            >
              Отмена
            </button>
            <button
              type="button"
              className={s.repostDialogSubmit}
              onClick={submit}
              disabled={busy}
            >
              {busy ? "Публикация…" : "Опубликовать"}
            </button>
          </footer>
        </div>
      </div>,
      document.body,
    );
  }

  return createPortal(
    <div
      className={s.repostSheetOverlay}
      role="dialog"
      aria-modal="true"
      aria-labelledby="repost-sheet-title"
      onClick={() => !busy && onClose?.()}
    >
      <div className={s.repostSheet} onClick={(e) => e.stopPropagation()}>
        <header className={s.repostSheetHeaderV2}>
          <button
            type="button"
            className={s.repostSheetBack}
            onClick={onClose}
            disabled={busy}
            aria-label="Назад"
          >
            <BackIcon />
          </button>
          <h2 id="repost-sheet-title" className={s.repostSheetTitle}>
            Репост
          </h2>
          <span className={s.repostSheetHeaderSpacer} aria-hidden />
        </header>

        <div className={s.repostSheetCompose}>
          <textarea
            ref={inputRef}
            id="repost-comment"
            className={s.repostSheetInput}
            rows={3}
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            placeholder="Поделитесь своими мыслями…"
            maxLength={2000}
            disabled={busy}
          />
        </div>

        <RepostPreview post={post} />

        <div className={s.repostSheetFooter}>
          <button
            type="button"
            className={s.repostSheetPublish}
            onClick={submit}
            disabled={busy}
          >
            {busy ? "Публикация…" : "Опубликовать"}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
